/**
 * Combat.
 *
 * Every tactic states how it reads the die, because the whole point of the system
 * is that a 6 is not always the best face (combat.ts). Guard rewards a low roll;
 * feint rewards an odd one. If the UI hid that, the tactics would look like flavour.
 */

import type { CombatTactic, GameSession } from '@lastroll/engine';
import { monsterArt, producedArt } from './producedArt.js';

const TACTIC_LABEL: Record<CombatTactic, string> = {
  strike: '벤다',
  guard: '막는다',
  feint: '속인다',
  focus: '모은다',
  invoke: '기원한다',
  flee: '물러난다',
  spare: '살려준다',
  bargain: '거래한다',
  intimidate: '위협한다',
  recruit: '영입한다',
};

const TACTIC_HINT: Record<CombatTactic, string> = {
  strike: '눈 + 힘. 모아둔 것을 쏟는다',
  guard: '낮은 눈일수록 반격이 커진다',
  feint: '홀수 눈에서 가장 강하다',
  focus: '이번 눈을 다음 일격에 저축',
  invoke: '체력이 낮으면 회복, 아니면 타격',
  flee: '실패가 아니라 전략이다',
  spare: '자비. 경험이 더 쌓인다',
  bargain: '무언가를 주고 길을 얻는다',
  intimidate: '상대의 성격에 따라 필요 눈이 다르다',
  recruit: '먼저 자제를 보여야 한다',
};

const MERCY: CombatTactic[] = ['spare', 'bargain', 'recruit'];

export interface CombatPanelProps {
  session: GameSession;
  onFight: (tactic: CombatTactic) => void;
  onRoll: () => void;
}

export function CombatPanel({ session, onFight, onRoll }: CombatPanelProps): JSX.Element | null {
  const combat = session.combat;
  const monster = session.activeMonster;
  if (!combat || !monster) return null;

  const pending = session.pendingRoll;
  const backdrop = monster.tier === 'boss' ? producedArt(session.currentRoom?.assetId) : null;
  const tactics = session.content.monsters.has(monster.id)
    ? availableTacticsFor(session)
    : [];

  return (
    <div className="panel">
      {/* 몬스터 그림 — 받은 그림이 있을 때만. 전에는 전투 창에 몬스터를 그릴 자리 자체가 없었다. */}
      {monsterArt(monster.id) && (
        // 보스는 제 방 앞에 선다 — 보스방 배경(tile.hollowcrown.throne)이 방 배너에만 있으면
        // 들어서자마자 전투라 거의 보이지 않았다(절대 규칙 11).
        <div
          className={`monster__stage${backdrop ? ' monster__stage--backdrop' : ''}`}
          style={backdrop ? { backgroundImage: `url(${backdrop})` } : undefined}
        >
          <img className="monster__art" src={monsterArt(monster.id)!} alt={monster.name} />
        </div>
      )}
      <h2 className="panel__title">{monster.name}</h2>
      {monster.description && <p className="panel__body">{monster.description}</p>}
      {combat.phase > 1 && (
        <p className="muted">2단계 — 쓰러진 줄 알았던 것이 다시 일어섰다.</p>
      )}

      <div className="bars">
        <div>
          <div className="bar__row">
            <span>{monster.name}</span>
            <span>
              {combat.monster.hp}/{combat.monster.maxHp}
            </span>
          </div>
          <div className="bar">
            <div
              className="bar__fill bar__fill--monster"
              style={{ width: `${(combat.monster.hp / combat.monster.maxHp) * 100}%` }}
            />
          </div>
        </div>
        <div>
          <div className="bar__row">
            <span>나</span>
            <span>
              {combat.player.hp}/{combat.player.maxHp}
            </span>
          </div>
          <div className="bar">
            <div
              className="bar__fill bar__fill--player"
              style={{ width: `${(combat.player.hp / combat.player.maxHp) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {(combat.player.focus > 0 || combat.player.counter > 0) && (
        <p className="muted">
          {combat.player.focus > 0 && `모은 힘 ${combat.player.focus} `}
          {combat.player.counter > 0 && `· 반격 ${combat.player.counter}`}
        </p>
      )}

      {combat.log.length > 0 && (
        <p className="panel__body">{combat.log[combat.log.length - 1]!.text}</p>
      )}

      {!pending ? (
        <button type="button" className="roll-btn" onClick={onRoll}>
          주사위를 굴린다
        </button>
      ) : (
        <>
          <p className="muted">
            굴린 눈 <strong style={{ color: 'var(--candle)' }}>{pending.finalFace}</strong> — 어떻게
            쓸 것인가.
          </p>
          <div className="tactics">
            {tactics.map((t) => (
              <button
                key={t}
                type="button"
                className={`tactic${MERCY.includes(t) ? ' tactic--mercy' : ''}${t === 'flee' ? ' tactic--flee' : ''}`}
                onClick={() => onFight(t)}
              >
                <span>{TACTIC_LABEL[t]}</span>
                <span className="tactic__hint">{TACTIC_HINT[t]}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Thin wrapper so the component does not import the combat module directly. */
function availableTacticsFor(session: GameSession): CombatTactic[] {
  const monster = session.activeMonster;
  const combat = session.combat;
  if (!monster || !combat) return [];
  const base: CombatTactic[] = ['strike', 'guard', 'feint', 'focus', 'invoke', 'flee'];
  const extra = monster.resolutions.filter((t) => {
    if (t === 'spare' || t === 'recruit') return combat.monster.hp <= monster.hp * 0.35;
    if (t === 'intimidate') return combat.monster.hp <= monster.hp * 0.5;
    return true;
  });
  return [...new Set([...base, ...extra])];
}
